import * as React from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { Button } from "../bits/Button/Button";
import { getVersionLink } from "../../../navigation/versioning";
import { AppRoute } from "../../../navigation/routes";
import { firebaseAuth } from "../../../utils/firestore";
import UserIcon from "../icons/User";
import { cn } from "../../helpers";
// import VersionToggleButton from "../../../common/VersionToggleButton";
import { Dropdown } from "../bits/Dropdown/Dropdown";
import styles from "./Navbar.module.scss";
import ChevronDownIcon from "../icons/ChevronDown";
import ClockIcon from "../icons/Clock";
import LogoutIcon from "../icons/Logout";
import ClientsIcon from "../icons/Navbar/Clients";
import CliniciansIcon from "../icons/Navbar/Clinicians";
import AvailabilityIcon from "../icons/Navbar/Availability";
import { hasOfficeClinicianBetaAccess } from "../../../appSettings";
import { AclContext } from "../../../contexts/AclContext";
import ManagementIcon from "../icons/Navbar/Management";
import { hasOfficeAdminBetaAccess } from "../../../appSettings";

export default function Navbar({ allowedRoutes }: { allowedRoutes: Set<String> }) {
    const { pathname } = useLocation();
    const authUser = React.useContext(AclContext)?.authUser;
    const loggedInUserEmail = authUser?.user?.email;
    const acl = authUser?.acl;
    const roles = [
        acl?.super_admin === true && "Super Admin",
        Boolean(authUser?.clinician) && "Clinician",
        authUser?.clinician?.is_supervisor === true && "Supervisor",
        (acl?.billing === true || acl?.insurance_verification === true) && "Billing",
        acl?.intake === true && "Intake",
        acl?.care_coordinator === true && "Care Coordinator",
        acl?.copay === true && "Copay",
        acl?.view_applicable_fees === true && "View Applicable Fees",
        acl?.office_manager === true && "Office Manager",
    ].filter((role): role is string => typeof role === "string");
    const history = useHistory();
    const isNavActive = (matchRoutes: string[]) =>
        matchRoutes?.some((route) => pathname === `/v2${route}`);

    const filterRoutesByPermission = (routes: any[]) => {
        return routes.filter((route) => {
            if (!route.route) return true;
            return allowedRoutes.has(route.route);
        });
    };

    const filterSectionsByPermission = (sections: any[]) => {
        return sections.filter((section) => {
            const visibleRoutes = filterRoutesByPermission(section.routes);
            return visibleRoutes.length > 0;
        });
    };

    const navSections = [
        {
            key: "clients",
            title: "Clients",
            icon: <ClientsIcon />,
            // Client records live under Clients even though they aren't menu items
            extraMatchRoutes: [
                AppRoute.CLIENT_FULL_VIEW,
                AppRoute.INACTIVE_CLIENT_VIEW,
            ],
            routes: [
                {
                    key: "intake-queue",
                    title: "Intake Queue",
                    to: getVersionLink(AppRoute.INTAKE_QUEUE, "v2"),
                    matchRoutes: ["/clients"],
                    route: AppRoute.INTAKE_QUEUE,
                },
                {
                    key: "booked-clients",
                    title: "Booked Clients",
                    to: getVersionLink(AppRoute.BOOKED_CLIENTS, "v2"),
                    matchRoutes: ["/booked-clients"],
                    route: AppRoute.BOOKED_CLIENTS,
                },
                {
                    key: "client-master-list",
                    title: "Client Master List",
                    to: getVersionLink(AppRoute.CLIENT_MASTER_LIST, "v2"),
                    matchRoutes: ["/client-master-list"],
                    route: AppRoute.CLIENT_MASTER_LIST,
                },
            ],
        },
        {
            key: "clinicians",
            title: "Clinicians",
            icon: <CliniciansIcon />,
            routes: [
                {
                    key: "clinicians-main",
                    title: "Manage Clinicians",
                    to: getVersionLink(AppRoute.CLINICIANS, "v2"),
                    matchRoutes: ["/clinicians"],
                    route: AppRoute.CLINICIANS,
                },
                {
                    key: "find-clinician",
                    title: "Find Clinician",
                    to: getVersionLink(AppRoute.FIND_CLINICIAN, "v2"),
                    matchRoutes: ["/find-clinician"],
                    route: AppRoute.FIND_CLINICIAN,
                },
                {
                    key: "supervisees",
                    title: "Supervisees",
                    to: getVersionLink(AppRoute.SUPERVISEES, "v2"),
                    matchRoutes: ["/supervisees"],
                    route: AppRoute.SUPERVISEES,
                },
                ...(hasOfficeClinicianBetaAccess(loggedInUserEmail as string)
                    ? [
                          {
                              key: "request-office-window",
                              title: "Request Office Window",
                              to: getVersionLink(
                                  AppRoute.REQUEST_OFFICE_WINDOW,
                                  "v2"
                              ),
                              matchRoutes: ["/request-office-window"],
                              route: AppRoute.REQUEST_OFFICE_WINDOW,
                          },
                      ]
                    : []),
            ],
        },
        {
            key: "availability",
            title: "Availability",
            icon: <AvailabilityIcon />,
            routes: [
                {
                    key: "office-availability",
                    title: "Office Availability",
                    to: getVersionLink(AppRoute.OFFICE_WINDOWS, "v2"),
                    matchRoutes: [AppRoute.OFFICE_WINDOWS],
                    disabled: false,
                    route: AppRoute.OFFICE_WINDOWS,
                },
                {
                    key: "clinician-availability",
                    title: "Clinician Availability",
                    to: getVersionLink(AppRoute.CLINICIAN_AVAILABILITY, "v2"),
                    matchRoutes: [AppRoute.CLINICIAN_AVAILABILITY],
                    route: AppRoute.CLINICIAN_AVAILABILITY,
                    disabled: false,
                },
            ],
        },
        {
            key: "management",
            title: "Management",
            icon: <ManagementIcon />,
            routes: [
                {
                    key: "experience-levels",
                    group: "setup",
                    title: "Experience Levels",
                    to: getVersionLink(AppRoute.EXPERIENCE_CONFIG, "v2"),
                    matchRoutes: [AppRoute.EXPERIENCE_CONFIG],
                    route: AppRoute.EXPERIENCE_CONFIG,
                },
                {
                    key: "intake-other",
                    group: "setup",
                    title: "Intake Other",
                    to: getVersionLink(AppRoute.INTAKE_OTHER, "v2"),
                    matchRoutes: [AppRoute.INTAKE_OTHER],
                    route: AppRoute.INTAKE_OTHER,
                },
                {
                    key: "email-signatures",
                    group: "email",
                    title: "Email Signatures",
                    to: getVersionLink(AppRoute.EMAIL_SIGNATURES, "v2"),
                    matchRoutes: [AppRoute.EMAIL_SIGNATURES],
                    route: AppRoute.EMAIL_SIGNATURES,
                },
                {
                    key: "care-coordinators",
                    group: "people",
                    title: "Care coordinators",
                    to: getVersionLink(AppRoute.CARE_COORDINATORS, "v2"),
                    matchRoutes: [AppRoute.CARE_COORDINATORS],
                    route: AppRoute.CARE_COORDINATORS,
                },
                {
                    key: "user-management",
                    group: "people",
                    title: "User Management",
                    to: getVersionLink(AppRoute.USER_MANAGEMENT, "v2"),
                    matchRoutes: [AppRoute.USER_MANAGEMENT],
                    route: AppRoute.USER_MANAGEMENT,
                },
                {
                    key: "sent-emails",
                    group: "email",
                    title: "Sent Emails",
                    to: getVersionLink(AppRoute.OUTBOUND_EMAILS, "v2"),
                    matchRoutes: [AppRoute.OUTBOUND_EMAILS],
                    route: AppRoute.OUTBOUND_EMAILS,
                },
                ...(hasOfficeAdminBetaAccess(loggedInUserEmail as string)
                    ? [
                          {
                              key: "office-management",
                              group: "setup",
                              title: "Office Management",
                              to: getVersionLink(AppRoute.OFFICE_MANAGEMENT, "v2"),
                              matchRoutes: ["/office-management"],
                              route: AppRoute.OFFICE_MANAGEMENT,
                          },
                      ]
                    : []),
                {
                    key: "configurations",
                    group: "setup",
                    title: "Configurations",
                    to: getVersionLink(AppRoute.CONFIGURATIONS_HOME, "v2"),
                    matchRoutes: [AppRoute.CONFIGURATIONS_HOME],
                    route: AppRoute.CONFIGURATIONS_HOME,
                },
                {
                    key: "diagnostics",
                    group: "tools",
                    title: "Diagnostics",
                    to: getVersionLink(AppRoute.DIAGNOSTICS, "v2"),
                    matchRoutes: [AppRoute.DIAGNOSTICS],
                    route: AppRoute.DIAGNOSTICS,
                },
            ],

            disabled: false,
        },
    ];

    const MANAGEMENT_GROUP_ORDER = ["people", "email", "setup", "tools"];
    const orderRoutes = (routes: any[]) =>
        routes.some((route) => route.group)
            ? [...routes].sort(
                  (a, b) =>
                      MANAGEMENT_GROUP_ORDER.indexOf(a.group) -
                      MANAGEMENT_GROUP_ORDER.indexOf(b.group)
              )
            : routes;

    const currentUser = firebaseAuth.currentUser;
    const avatar = currentUser?.photoURL ? (
        <img src={currentUser.photoURL} alt="" className={styles.userImage} />
    ) : (
        <span className={styles.fallbackAvatar} aria-hidden="true">
            {currentUser?.displayName?.[0]?.toUpperCase() || <UserIcon />}
        </span>
    );

    return (
        <header className={styles.topBar}>
            <div className={styles.leftSection}>
                <div className={styles.systemLabel}>
                    <span className={styles.systemLabelText}>IS</span>
                    Intake System
                    {process.env.REACT_APP_INTAKE_VERSION && (
                        <span className={styles.versionLabel}>
                            v{process.env.REACT_APP_INTAKE_VERSION}
                        </span>
                    )}
                </div>
                <nav className={styles.navLinks} aria-label="Main">
                    {filterSectionsByPermission(navSections).map((section) => {
                        const visibleRoutes = orderRoutes(
                            filterRoutesByPermission(section.routes)
                        );
                        const isActive = isNavActive([
                            ...visibleRoutes.flatMap((route) => route.matchRoutes),
                            ...(section.extraMatchRoutes || []),
                        ]);

                        const navClass = cn(styles.navButton, {
                            [styles.active]: isActive,
                            [styles.disabled]: section.disabled,
                        });

                        if (section.disabled) {
                            return (
                                <div key={section.key} className={navClass}>
                                    {section.icon}
                                    {section.title}
                                </div>
                            );
                        }

                        return (
                            <Dropdown key={section.key} triggerOnHover={true}>
                                <Dropdown.Trigger asChild>
                                    <Button
                                        className={navClass}
                                        variant="normal"
                                        iconLeft={section.icon}
                                        iconRight={
                                            <ChevronDownIcon stroke="currentColor" />
                                        }
                                        aria-current={isActive ? "page" : undefined}
                                    >
                                        {section.title}
                                    </Button>
                                </Dropdown.Trigger>
                                <Dropdown.Content align="start" width="auto">
                                    <div className={styles.menu}>
                                        {visibleRoutes.map((route, index) => {
                                            const isRouteActive = isNavActive(
                                                route.matchRoutes
                                            );
                                            const startsGroup =
                                                index > 0 &&
                                                route.group &&
                                                route.group !==
                                                    visibleRoutes[index - 1].group;
                                            return (
                                                <React.Fragment key={route.key}>
                                                    {startsGroup && (
                                                        <Dropdown.Separator />
                                                    )}
                                                    <Dropdown.Item
                                                        isSelected={isRouteActive}
                                                        className={styles.menuItem}
                                                        onClick={() => {
                                                            if (
                                                                route.to &&
                                                                !route.disabled
                                                            ) {
                                                                history.push(
                                                                    route.to
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        {route.disabled ? (
                                                            <span
                                                                className={
                                                                    styles.disabledRoute
                                                                }
                                                            >
                                                                {route.title}
                                                            </span>
                                                        ) : (
                                                            <NavLink
                                                                to={route.to}
                                                                className={
                                                                    styles.dropdownNavLink
                                                                }
                                                                aria-current={
                                                                    isRouteActive
                                                                        ? "page"
                                                                        : undefined
                                                                }
                                                            >
                                                                {route.title}
                                                            </NavLink>
                                                        )}
                                                    </Dropdown.Item>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </Dropdown.Content>
                            </Dropdown>
                        );
                    })}
                </nav>
            </div>

            <div className={styles.rightSection}>
                {currentUser && (
                    <Dropdown>
                        <Dropdown.Trigger asChild>
                            <button
                                type="button"
                                className={styles.userInfo}
                                aria-label="Account menu"
                            >
                                {avatar}
                                <span className={styles.userName}>
                                    {currentUser.displayName}
                                </span>
                                <ChevronDownIcon stroke="currentColor" />
                            </button>
                        </Dropdown.Trigger>
                        <Dropdown.Content align="end">
                            <div className={styles.dropdownContent}>
                                <div className={styles.profileHeader}>
                                    {avatar}
                                    <div className={styles.profileText}>
                                        <span className={styles.profileName}>
                                            {currentUser.displayName}
                                        </span>
                                        {currentUser.email && (
                                            <span className={styles.profileEmail}>
                                                {currentUser.email}
                                            </span>
                                        )}
                                        {roles.length > 0 && (
                                            <span className={styles.profileRoles}>
                                                <span className="sr-only">
                                                    Roles:{" "}
                                                </span>
                                                {roles.join(" · ")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {allowedRoutes.has(
                                    getVersionLink(AppRoute.TIME_OFF, "v2")
                                ) && (
                                    <Dropdown.Item
                                        className={styles.accountMenuItem}
                                        isSelected={
                                            pathname ===
                                            getVersionLink(AppRoute.TIME_OFF, "v2")
                                        }
                                        aria-current={
                                            pathname ===
                                            getVersionLink(AppRoute.TIME_OFF, "v2")
                                                ? "page"
                                                : undefined
                                        }
                                        onClick={() =>
                                            history.push(
                                                getVersionLink(
                                                    AppRoute.TIME_OFF,
                                                    "v2"
                                                )
                                            )
                                        }
                                    >
                                        <ClockIcon />
                                        My time off
                                    </Dropdown.Item>
                                )}
                                <Button
                                    size="small"
                                    variant="normal"
                                    iconLeft={<LogoutIcon />}
                                    onClick={async () => {
                                        await firebaseAuth.signOut();
                                        const uiVersion =
                                            localStorage.getItem("ui_version");
                                        localStorage.clear();
                                        localStorage.setItem(
                                            "ui_version",
                                            uiVersion || "v1"
                                        );
                                    }}
                                    className={styles.logoutBtn}
                                >
                                    Log out
                                </Button>
                            </div>
                        </Dropdown.Content>
                    </Dropdown>
                )}
            </div>
        </header>
    );
}
